/**
 * Token Monitoring Service
 * Handles proactive token refresh and session management
 */

import { jwtUtils } from '../auth';

export interface TokenMonitorConfig {
  refreshBufferMinutes: number; // Refresh token this many minutes before expiration
  checkIntervalSeconds: number; // How often to check token status
  maxRetries: number; // Maximum refresh attempts
  onTokenRefresh?: (newToken: string) => void;
  onTokenExpired?: () => void;
  onRefreshFailed?: (error: Error) => void;
}

export class TokenMonitorService {
  private static instance: TokenMonitorService;
  private intervalId: NodeJS.Timeout | null = null;
  public isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;
  private config: TokenMonitorConfig;
  private retryCount = 0;

  private constructor(config: Partial<TokenMonitorConfig> = {}) {
    this.config = {
      refreshBufferMinutes: 5, // Refresh 5 minutes before expiration
      checkIntervalSeconds: 30, // Check every 30 seconds
      maxRetries: 3,
      ...config,
    };
  }

  public static getInstance(config?: Partial<TokenMonitorConfig>): TokenMonitorService {
    if (!TokenMonitorService.instance) {
      TokenMonitorService.instance = new TokenMonitorService(config);
    }
    return TokenMonitorService.instance;
  }

  /**
   * Start monitoring token expiration
   */
  public startMonitoring(): void {
    if (this.intervalId) {
      console.log('🔄 TokenMonitor: Already monitoring, skipping start');
      return;
    }

    console.log('🚀 TokenMonitor: Starting token monitoring');
    this.intervalId = setInterval(() => {
      this.checkTokenStatus();
    }, this.config.checkIntervalSeconds * 1000);

    // Check immediately
    this.checkTokenStatus();
  }

  /**
   * Stop monitoring token expiration
   */
  public stopMonitoring(): void {
    if (this.intervalId) {
      console.log('🛑 TokenMonitor: Stopping token monitoring');
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Check current token status and refresh if needed
   */
  private async checkTokenStatus(): Promise<void> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.log('🔍 TokenMonitor: No access token found');
      return;
    }

    // Check if token is already expired
    if (jwtUtils.isExpired(token)) {
      console.log('⚠️ TokenMonitor: Token is expired, attempting refresh');
      await this.handleExpiredToken();
      return;
    }

    // Check if token will expire soon
    if (jwtUtils.willExpireWithin(token, this.config.refreshBufferMinutes)) {
      console.log(`🔄 TokenMonitor: Token expires within ${this.config.refreshBufferMinutes} minutes, refreshing proactively`);
      await this.handleProactiveRefresh();
      return;
    }

    const timeLeft = jwtUtils.getTimeUntilExpiration(token);
    console.log(`✅ TokenMonitor: Token valid for ${Math.floor(timeLeft / 60)} minutes`);
  }

  /**
   * Handle expired token
   */
  private async handleExpiredToken(): Promise<void> {
    if (this.isRefreshing) {
      console.log('🔄 TokenMonitor: Already refreshing, waiting...');
      return;
    }

    try {
      await this.performTokenRefresh();
      this.retryCount = 0; // Reset retry count on success
    } catch (error) {
      console.error('❌ TokenMonitor: Failed to refresh expired token:', error);
      this.config.onRefreshFailed?.(error as Error);
      this.config.onTokenExpired?.();
    }
  }

  /**
   * Handle proactive token refresh
   */
  private async handleProactiveRefresh(): Promise<void> {
    if (this.isRefreshing) {
      console.log('🔄 TokenMonitor: Already refreshing, skipping proactive refresh');
      return;
    }

    try {
      await this.performTokenRefresh();
      this.retryCount = 0; // Reset retry count on success
    } catch (error) {
      console.error('❌ TokenMonitor: Proactive refresh failed:', error);
      this.config.onRefreshFailed?.(error as Error);
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(): Promise<string> {
    if (this.isRefreshing && this.refreshPromise) {
      console.log('🔄 TokenMonitor: Waiting for existing refresh to complete');
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doTokenRefresh();

    try {
      const newToken = await this.refreshPromise;
      this.config.onTokenRefresh?.(newToken);
      return newToken;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Execute the token refresh API call
   */
  private async doTokenRefresh(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    console.log('🔄 TokenMonitor: Performing token refresh...');
    
    try {
      // Import the API client dynamically to avoid circular dependencies
      const { apiClient } = await import('../api/client');
      const response = await apiClient.refreshToken({ refresh_token: refreshToken });
      
      // Update tokens in localStorage
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      console.log('✅ TokenMonitor: Token refresh successful');
      return response.access_token;
    } catch (error) {
      this.retryCount++;
      console.error(`❌ TokenMonitor: Token refresh failed (attempt ${this.retryCount}/${this.config.maxRetries}):`, error);
      
      if (this.retryCount >= this.config.maxRetries) {
        console.error('❌ TokenMonitor: Max retries reached, clearing tokens');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        throw new Error('Token refresh failed after maximum retries');
      }
      
      // Wait before retrying (exponential backoff)
      const delay = Math.pow(2, this.retryCount) * 1000;
      console.log(`⏳ TokenMonitor: Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return this.doTokenRefresh();
    }
  }

  /**
   * Force immediate token refresh
   */
  public async forceRefresh(): Promise<string> {
    console.log('🔄 TokenMonitor: Force refresh requested');
    return this.performTokenRefresh();
  }


  /**
   * Get current token status
   */
  public getTokenStatus(): {
    isValid: boolean;
    isExpired: boolean;
    willExpireSoon: boolean;
    timeUntilExpiration: number;
    expirationDate: Date | null;
  } {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return {
        isValid: false,
        isExpired: true,
        willExpireSoon: true,
        timeUntilExpiration: 0,
        expirationDate: null,
      };
    }

    return {
      isValid: !jwtUtils.isExpired(token),
      isExpired: jwtUtils.isExpired(token),
      willExpireSoon: jwtUtils.willExpireWithin(token, this.config.refreshBufferMinutes),
      timeUntilExpiration: jwtUtils.getTimeUntilExpiration(token),
      expirationDate: jwtUtils.getExpirationDate(token),
    };
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<TokenMonitorConfig>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('🔧 TokenMonitor: Configuration updated', this.config);
  }
}

// Export singleton instance
export const tokenMonitor = TokenMonitorService.getInstance();
